// src/components/foundation/ExhibitionsPanel.jsx
// Onglet « Expositions » du tableau de bord Fondation : liste, création, modification,
// suppression d'expositions (temporaires ou permanentes) et choix des œuvres présentées.
import React, { useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { toast } from "react-hot-toast";

const TODAY = () => new Date().toISOString().slice(0, 10);

export function exhibitionStatus(e) {
  const today = TODAY();
  if (e.start_date && e.start_date > today) return "upcoming";
  if (e.end_date && e.end_date < today) return "ended";
  return "ongoing";
}

const STATUS = {
  ongoing: { label: "En cours", color: "#166534", bg: "#f0fdf4", border: "#bbf7d0" },
  upcoming: { label: "À venir", color: "#92400e", bg: "#fffbeb", border: "#fde68a" },
  ended: { label: "Terminée", color: "#6b7280", bg: "#f5f5f4", border: "#e7e5e4" },
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "";

const input = {
  padding: "8px 10px", border: "1px solid #e0d8cc", borderRadius: 3, fontSize: 13,
  fontFamily: "system-ui, sans-serif", outline: "none", width: "100%", boxSizing: "border-box",
};
const btn = {
  padding: "8px 16px", background: "#1a1a1a", color: "#fff", border: "1px solid #1a1a1a",
  borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "system-ui, sans-serif",
};
const btnGhost = { ...btn, background: "#fff", color: "#1a1a1a", border: "1px solid #e0d8cc" };
const label = { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#888", fontWeight: 700, fontFamily: "system-ui, sans-serif", marginBottom: 4 };

const EMPTY = { id: null, title: "", exhibition_type: "temporary", venue: "", curator: "", description: "", start_date: "", end_date: "" };

function ExhibitionForm({ userId, initial, initialArtworkIds, artworks, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [selected, setSelected] = useState(new Set(initialArtworkIds));
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return artworks.filter((a) => !q || `${a.title || ""} ${a.artist || ""} ${a.inventory_number || ""}`.toLowerCase().includes(q));
  }, [artworks, query]);

  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const save = async () => {
    if (!form.title.trim()) { toast.error("Donnez un titre à l'exposition."); return; }
    if (form.exhibition_type === "temporary" && form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error("La date de fin précède la date de début."); return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        exhibition_type: form.exhibition_type,
        venue: form.venue.trim() || null,
        curator: form.curator.trim() || null,
        description: form.description.trim() || null,
        start_date: form.start_date || null,
        end_date: form.exhibition_type === "permanent" ? null : form.end_date || null,
        updated_at: new Date().toISOString(),
      };
      let id = form.id;
      if (id) {
        const { error } = await supabase.from("foundation_exhibitions").update(payload).eq("id", id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from("foundation_exhibitions").insert({ ...payload, owner_id: userId }).select("id").single();
        if (error) throw error;
        id = data.id;
      }
      const { error: delErr } = await supabase.from("foundation_exhibition_items").delete().eq("exhibition_id", id);
      if (delErr) throw delErr;
      const rows = Array.from(selected).map((artwork_id, i) => ({ exhibition_id: id, artwork_id, display_order: i }));
      if (rows.length) {
        const { error: insErr } = await supabase.from("foundation_exhibition_items").insert(rows);
        if (insErr) throw insErr;
      }
      toast.success("Exposition enregistrée.");
      onSaved();
    } catch (e) {
      toast.error("Enregistrement impossible : " + (e.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ border: "1px solid #e0d8cc", borderRadius: 8, background: "#fff", padding: 20, marginBottom: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={label}>Titre *</div>
          <input style={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex. : Regards sur la collection" />
        </div>
        <div>
          <div style={label}>Type</div>
          <select style={input} value={form.exhibition_type} onChange={(e) => setForm({ ...form, exhibition_type: e.target.value })}>
            <option value="temporary">Temporaire</option>
            <option value="permanent">Permanente</option>
          </select>
        </div>
        <div>
          <div style={label}>Lieu / salle</div>
          <input style={input} value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} placeholder="Ex. : Grand Salon" />
        </div>
        <div>
          <div style={label}>Début</div>
          <input style={input} type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
        </div>
        <div>
          <div style={label}>Fin</div>
          <input style={input} type="date" disabled={form.exhibition_type === "permanent"} value={form.exhibition_type === "permanent" ? "" : form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
        </div>
        <div>
          <div style={label}>Commissaire</div>
          <input style={input} value={form.curator} onChange={(e) => setForm({ ...form, curator: e.target.value })} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={label}>Description</div>
          <textarea style={{ ...input, resize: "vertical" }} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={label}>Œuvres présentées ({selected.size})</div>
        <input style={{ ...input, marginBottom: 8 }} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher une œuvre, un artiste, un numéro d'inventaire…" />
        <div style={{ maxHeight: 240, overflowY: "auto", border: "1px solid #f0ece5", borderRadius: 4 }}>
          {shown.length === 0 && <div style={{ padding: 14, color: "#aaa", fontSize: 13, fontFamily: "system-ui" }}>Aucune œuvre trouvée dans vos collections.</div>}
          {shown.map((a) => (
            <label key={a.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 12px", borderBottom: "1px solid #f7f4ee", cursor: "pointer", fontFamily: "system-ui", fontSize: 13 }}>
              <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggle(a.id)} />
              <span><strong style={{ fontWeight: 600 }}>{a.title || "Sans titre"}</strong>{a.artist ? ` — ${a.artist}` : ""}{a.inventory_number ? ` [${a.inventory_number}]` : ""}</span>
            </label>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <button style={btn} onClick={save} disabled={saving}>{saving ? "Enregistrement…" : "Enregistrer"}</button>
        <button style={btnGhost} onClick={onClose} disabled={saving}>Annuler</button>
      </div>
    </div>
  );
}

export default function ExhibitionsPanel({ userId, exhibitions, itemsByExhibition, artworks, onReload, readOnly = false }) {
  const [editing, setEditing] = useState(null);

  const sorted = useMemo(() => {
    const rank = { ongoing: 0, upcoming: 1, ended: 2 };
    return [...exhibitions].sort((a, b) => rank[exhibitionStatus(a)] - rank[exhibitionStatus(b)] || (b.start_date || "").localeCompare(a.start_date || ""));
  }, [exhibitions]);

  const remove = async (e) => {
    if (!window.confirm(`Supprimer l'exposition « ${e.title} » ? Les œuvres ne sont pas supprimées.`)) return;
    const { error } = await supabase.from("foundation_exhibitions").delete().eq("id", e.id);
    if (error) toast.error(error.message); else { toast.success("Exposition supprimée."); onReload(); }
  };

  const artworkTitle = (id) => artworks.find((a) => a.id === id)?.title || "Œuvre";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
        <p style={{ margin: 0, fontSize: 13, color: "#888", fontFamily: "system-ui, sans-serif" }}>
          Expositions temporaires et permanentes, avec les œuvres présentées.
        </p>
        {!editing && !readOnly && <button style={btn} onClick={() => setEditing({ form: EMPTY, ids: [] })}>+ Nouvelle exposition</button>}
      </div>

      {editing && (
        <ExhibitionForm
          key={editing.form.id || "new"}
          userId={userId}
          initial={editing.form}
          initialArtworkIds={editing.ids}
          artworks={artworks}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onReload(); }}
        />
      )}

      {sorted.length === 0 && !editing && (
        <div style={{ textAlign: "center", padding: "48px 24px" }}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 36, color: "#e0d8cc", marginBottom: 12 }}>◫</div>
          <p style={{ fontFamily: "Georgia, serif", fontSize: 17, color: "#1a1a1a", margin: "0 0 6px" }}>Aucune exposition</p>
          <p style={{ fontSize: 13, color: "#888", fontFamily: "system-ui", margin: 0 }}>Créez une exposition et choisissez les œuvres qui y sont présentées.</p>
        </div>
      )}

      {sorted.map((e) => {
        const st = STATUS[exhibitionStatus(e)];
        const ids = itemsByExhibition[e.id] || [];
        return (
          <div key={e.id} style={{ border: "1px solid #e0d8cc", borderRadius: 8, background: "#fff", padding: "16px 20px", marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
                  <span style={{ fontFamily: "Georgia, serif", fontSize: 16, color: "#1a1a1a" }}>{e.title}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 3, textTransform: "uppercase", fontFamily: "system-ui", color: st.color, background: st.bg, border: `1px solid ${st.border}` }}>{st.label}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 2, textTransform: "uppercase", background: "#1c3a5e", color: "#fff", fontFamily: "system-ui" }}>
                    {e.exhibition_type === "permanent" ? "Permanente" : "Temporaire"}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "#888", fontFamily: "system-ui" }}>
                  {[e.venue, e.start_date ? `${fmtDate(e.start_date)}${e.end_date ? ` → ${fmtDate(e.end_date)}` : e.exhibition_type === "permanent" ? " → sans date de fin" : ""}` : null, `${ids.length} œuvre${ids.length !== 1 ? "s" : ""}`, e.curator ? `Commissaire : ${e.curator}` : null].filter(Boolean).join(" · ")}
                </div>
                {e.description && <div style={{ fontSize: 12, color: "#aaa", fontFamily: "Georgia, serif", fontStyle: "italic", marginTop: 4, maxWidth: 560 }}>{e.description}</div>}
                {ids.length > 0 && (
                  <div style={{ fontSize: 11, color: "#1c3a5e", fontFamily: "system-ui", marginTop: 6 }}>
                    {ids.slice(0, 4).map(artworkTitle).join(" · ")}{ids.length > 4 ? ` · +${ids.length - 4}` : ""}
                  </div>
                )}
              </div>
              {!readOnly && <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <button style={btnGhost} onClick={() => setEditing({ form: { ...EMPTY, ...e, venue: e.venue || "", curator: e.curator || "", description: e.description || "", start_date: e.start_date || "", end_date: e.end_date || "" }, ids })}>Modifier</button>
                <button style={{ ...btnGhost, color: "#dc2626" }} onClick={() => remove(e)}>Supprimer</button>
              </div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
